import { has } from 'lodash';
import { createWrapper } from 'src/careflow/ph-core/fhir-client/fhir-react/base';
import { EpisodeOfCare, Identifier, Reference } from 'src/careflow/ph-core/fhir-client/resources';
import { shortFormat } from 'src/utils/formatTime';

export enum IdentifierCode {
  MINT_PROGRAM_IDENTIFIER = 'MINT PROGRAM',
}

export const IDENTIFIER_MAP: Map<IdentifierCode, Identifier> = newIdentifierMap()
  .add(
    IdentifierCode.MINT_PROGRAM_IDENTIFIER,
    'MINT PROGRAM IDENTIFIER',
    process.env.REACT_APP_MINT_PROGRAM_IDENTIFIER
  )
  .getValue();

export const EpisodeOfCareWrapper = createWrapper('EpisodeOfCare', (episodeOfCare) => ({
  getMINT(systemUrl: string): Identifier | null {
    return getIdentifier(episodeOfCare, IdentifierCode.MINT_PROGRAM_IDENTIFIER, systemUrl);
  },
  getReferralRequestReference(): Reference[] {
    if (episodeOfCare?.referralRequest && episodeOfCare?.referralRequest?.length > 0) {
      const referralRequest = episodeOfCare?.referralRequest
        .filter((s) => has(s, 'reference') && s?.reference)
        .map((s) => s.reference) as Reference[];
      return referralRequest;
    }
    return [];
  },
  getFirstType(): { code: string; display: string } | null {
    const type = episodeOfCare?.type?.[0];

    if (type) {
      return {
        display: type?.text ?? type?.coding?.[0]?.display ?? '',
        code: type?.coding?.[0]?.code ?? '',
      };
    }

    return null;
  },
  isOngoning() {
    const isOngoing = episodeOfCare.status === 'active';

    return isOngoing ? 'Ongoing episode' : 'Completed episode';
  },
  getPeriodShorFormat() {
    const { start, end } = episodeOfCare?.period ?? {};

    return {
      start: start ? shortFormat(new Date(start)) : '',
      end: end ? shortFormat(new Date(end)) : '',
    };
  },
  getPeriod() {
    const { start, end } = episodeOfCare?.period ?? {};

    if (!start) return '';

    const startFormatted = shortFormat(new Date(start));

    if (episodeOfCare?.status === 'active') {
      return `${startFormatted} - Present`;
    }

    return end ? `${startFormatted} - ${shortFormat(new Date(end))}` : startFormatted;
  },
}));

function getIdentifier(
  episodeOfCare: EpisodeOfCare,
  identifierCode: IdentifierCode,
  systemUrl: string
): Identifier | null {
  let identifierType = IDENTIFIER_MAP.get(identifierCode);
  const system = systemUrl ? systemUrl : identifierType?.system;
  if (system) {
    const value =
      episodeOfCare.identifier?.find((identifier) => identifier?.system === system) || null;
    return value;
  }
  return null;
}

function newIdentifierMap() {
  const entries: Map<IdentifierCode, Identifier> = new Map();
  const map = {
    add: (code: IdentifierCode, display: string, system?: string) => {
      entries.set(code, {
        system,
        type: {
          coding: [
            {
              system,
              code,
              display,
            },
          ],
          text: display,
        },
      });
      return map;
    },
    getValue: () => entries,
  };
  return map;
}

export type WrappedEpisodeOfCare = ReturnType<typeof EpisodeOfCareWrapper>;
